import React, { useState } from 'react';
import { motion } from 'framer-motion';
import copy from 'copy-to-clipboard';
import Layout from '../components/Layout';
import Textarea from '../components/Textarea';
import Button from '../components/Button';
import { removeDuplicates } from '../utils/textUtils';

const DuplicateRemover: React.FC = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState('');
  const [mode, setMode] = useState<'lines' | 'words'>('lines');
  const [caseSensitive, setCaseSensitive] = useState(true);
  const [showToast, setShowToast] = useState(false);

  const handleRemove = () => {
    const cleaned = removeDuplicates[mode](text, caseSensitive);
    setResult(cleaned);
  };

  const handleCopy = () => {
    copy(result);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const getRemovedCount = () => {
    if (!text || !result) return 0;
    const original = mode === 'lines' ? text.split('\n') : text.split(/\s+/);
    const cleaned = mode === 'lines' ? result.split('\n') : result.split(/\s+/);
    return original.length - cleaned.length;
  };

  return (
    <Layout showBackButton>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-32"
        >
          <h1 className="text-4xl font-bold text-textPrimary mb-8">중복 제거</h1>
          <p className="text-textSecondary">
            중복된 줄이나 단어를 정리합니다
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-24"
        >
          <Textarea value={text} onChange={setText} />
        </motion.div>

        {text && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-24"
          >
            <div className="bg-surface rounded-large p-24 mb-24">
              <h3 className="text-lg font-bold text-textPrimary mb-16">옵션</h3>
              <div className="space-y-12">
                <div className="flex gap-12">
                  <label className="flex items-center gap-8 cursor-pointer">
                    <input
                      type="radio"
                      checked={mode === 'lines'}
                      onChange={() => setMode('lines')}
                      className="w-16 h-16"
                    />
                    <span className="text-textPrimary">중복 줄 제거</span>
                  </label>
                  <label className="flex items-center gap-8 cursor-pointer">
                    <input
                      type="radio"
                      checked={mode === 'words'}
                      onChange={() => setMode('words')}
                      className="w-16 h-16"
                    />
                    <span className="text-textPrimary">중복 단어 제거</span>
                  </label>
                </div>
                <label className="flex items-center gap-8 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!caseSensitive}
                    onChange={(e) => setCaseSensitive(!e.target.checked)}
                    className="w-16 h-16"
                  />
                  <span className="text-textPrimary">대소문자 구분 안함</span>
                </label>
              </div>
              <div className="mt-16">
                <Button onClick={handleRemove}>중복 제거하기</Button>
              </div>
            </div>

            {result && (
              <>
                <div className="bg-surface rounded-large p-16 mb-16 text-center">
                  <span className="text-textSecondary text-sm">제거된 중복: </span>
                  <span className="text-2xl font-bold text-primary">
                    {getRemovedCount()}개
                  </span>
                </div>

                <div>
                  <label className="block text-textSecondary mb-8 text-sm">
                    정리된 텍스트
                  </label>
                  <Textarea value={result} onChange={setResult} />
                  <div className="mt-12">
                    <Button onClick={handleCopy}>결과 복사</Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed bottom-32 left-1/2 transform -translate-x-1/2 bg-textPrimary text-white px-24 py-12 rounded-medium shadow-lg"
          >
            ✓ 복사됨
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default DuplicateRemover;
